import {
    Breadcrumb as AriaBreadcrumb,
    type BreadcrumbProps as AriaBreadcrumbProps,
    Breadcrumbs as AriaBreadcrumbs,
    type BreadcrumbsProps as AriaBreadcrumbsProps,
    Link as AriaLink,
    composeRenderProps,
} from 'react-aria-components';

import { classnames } from '@/utils/classnames.ts';

const BreadcrumbsRoot = <T extends object>({ className, ...props }: AriaBreadcrumbsProps<T>) => (
    <AriaBreadcrumbs {...props} className={classnames('flex min-w-0 items-center gap-2 text-sm', className)} />
);

export interface BreadcrumbsItemProps extends Omit<AriaBreadcrumbProps, 'children'> {
    // Without one, it's only named, e.g. where its page can't be opened.
    href?: string;
    children: React.ReactNode;
}

const BreadcrumbsItem = ({ href, children, className, ...props }: BreadcrumbsItemProps) => (
    <AriaBreadcrumb
        {...props}
        className={composeRenderProps(className, (className) =>
            classnames(
                'flex min-w-0 items-center gap-2 text-text-muted after:text-text-subtle after:content-["/"] last:text-text last:after:content-none',
                className
            )
        )}
    >
        {href ? (
            <AriaLink
                href={href}
                className="truncate rounded outline-none hover:text-text data-[current]:hover:text-text data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary"
            >
                {children}
            </AriaLink>
        ) : (
            <span className="truncate">{children}</span>
        )}
    </AriaBreadcrumb>
);

// Between what's before the breadcrumbs, e.g. the workspace's switcher, and the first of them, like between them.
const BreadcrumbsSeparator = () => (
    <span aria-hidden className="text-sm text-text-subtle">
        /
    </span>
);

export default Object.assign(BreadcrumbsRoot, {
    Item: BreadcrumbsItem,
    Separator: BreadcrumbsSeparator,
});
